import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraftModule from "html-to-draftjs";

// html-to-draftjs is CommonJS; under Vite's ESM interop the function may arrive as `.default`.
const htmlToDraft = htmlToDraftModule.default ?? htmlToDraftModule;

/* Rich text editor for announcements — replaces legacy uiComponents WysiwygEditor. */
export function WysiwygEditor({ name, control, errors, initialHtml }) {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Seed the editor from pre-existing HTML (e.g. Edit & resubmit prefill) once it arrives. The editor
  // owns its own draft-js state and does not read the form value, so without this the prefilled body
  // renders blank. Seed only once so it never clobbers the user's edits.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current || !initialHtml) return;
    // Guard the parse: a malformed/unexpected draft body must degrade to an empty editor, not crash the
    // whole modal during render commit.
    try {
      const { contentBlocks, entityMap } = htmlToDraft(initialHtml);
      setEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(contentBlocks, entityMap)));
    } catch {
      setEditorState(EditorState.createEmpty());
    }
    seeded.current = true;
  }, [initialHtml]);

  const hasError = Boolean(errors?.[name]);
  const selectionState = editorState.getSelection().getHasFocus();

  const borderColor = hasError
    ? "var(--red)"
    : selectionState
      ? "var(--green-success)"
      : "var(--border)";

  const wrapperStyle = {
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    height: "100%",
  };

  const editorStyle = {
    marginBottom: 0,
    padding: "0 15px 0",
    flex: "2 1 auto",
    height: "100%",
  };

  const toolbarStyle = {
    border: "none",
    borderBottom: "1px solid var(--divider)",
    marginBottom: 0,
    backgroundColor: "var(--grey-bg)",
  };

  const getFileBase64 = async (file, callback) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result);
  };

  const uploadCallback = (file) =>
    new Promise((resolve) => {
      getFileBase64(file, (data) => {
        resolve({ data: { link: data } });
      });
    });

  const errorMessage = errors?.[name]
    ? Array.isArray(errors[name])
      ? errors[name][0]?.message
      : errors[name]?.message
    : null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur } }) => (
        <div style={{ marginBottom: 23, height: "100%" }}>
          <Editor
            editorState={editorState}
            onBlur={onBlur}
            wrapperStyle={wrapperStyle}
            editorStyle={editorStyle}
            toolbarStyle={toolbarStyle}
            onEditorStateChange={(editState) => {
              setEditorState(editState);
              if (editState.getCurrentContent().hasText()) {
                const html = draftToHtml(convertToRaw(editState.getCurrentContent()));
                onChange(html);
              } else {
                onChange("");
              }
            }}
            toolbar={{
              image: {
                previewImage: true,
                uploadCallback,
                inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
                defaultSize: {
                  height: "auto",
                  width: "100%",
                },
              },
            }}
          />
          {errorMessage ? (
            <p style={{ fontSize: 10, margin: "8px 0 0", color: "var(--red)" }}>{errorMessage}</p>
          ) : null}
        </div>
      )}
    />
  );
}

export default WysiwygEditor;
